import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  Clock, 
  Calendar, 
  User, 
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
} from 'lucide-react';
import { timesheetAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const EmployeeTimesheetDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [timesheetIdSearch, setTimesheetIdSearch] = useState('');
  const [dateSearch, setDateSearch] = useState('');
  const [sortMode, setSortMode] = useState('STATUS'); // STATUS | DATE_DESC | DATE_ASC
  const NEEDS_APPROVAL = 'NEEDS_APPROVAL';

  // Determine the correct API endpoint based on user role
  const getUserTimesheetsEndpoint = () => {
    const userRole = typeof currentUser?.role === 'object' ? currentUser?.role?.name : currentUser?.role;
    if (userRole === 'SUPER_ADMIN') {
      return timesheetAPI.getSuperAdminUserTimesheets(userId);
    } else if (userRole === 'MANAGER') {
      return timesheetAPI.getManagerUserTimesheets(userId);
    } else {
      return timesheetAPI.getUserTimesheets(userId);
    }
  };

  const { data, isLoading, error } = useQuery(
    ['user-timesheets', userId, currentUser?.role],
    getUserTimesheetsEndpoint,
    {
      select: (response) => response.data,
      enabled: !!userId && !!currentUser,
      onError: (error) => {
        toast.error('Failed to load user timesheets: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const user = data?.user;
  const timesheets = data?.timesheets || [];

  const filteredTimesheets = timesheets.filter(timesheet => {
    // Status filter
    if (statusFilter !== 'ALL') {
      // "Needs Approval" grouped view: SUBMITTED + REUPDATED (only items that require action)
      if (statusFilter === NEEDS_APPROVAL) {
        const approvalNeededStatuses = ['SUBMITTED', 'REUPDATED'];
        if (!approvalNeededStatuses.includes(timesheet.status)) {
          return false;
        }
      } else if (timesheet.status !== statusFilter) {
        return false;
      }
    }

    // Timesheet ID search
    const hasTimesheetIdSearch = timesheetIdSearch && timesheetIdSearch.trim() !== '';
    if (hasTimesheetIdSearch) {
      const searchId = String(timesheetIdSearch).trim();
      const timesheetId = timesheet.id ? String(timesheet.id) : '';
      if (!timesheetId.includes(searchId)) {
        return false;
      }
    }

    // Date search - calendar date picker format (yyyy-MM-dd)
    const hasDateSearch = dateSearch && dateSearch.trim() !== '';
    if (hasDateSearch) {
      try {
        const searchDate = new Date(dateSearch);
        const searchDateStr = format(searchDate, 'yyyy-MM-dd');
        
        let matchesDate = false;
        
        // Check if search date matches start date
        if (timesheet.startDate) {
          const startDate = new Date(timesheet.startDate);
          const startDateStr = format(startDate, 'yyyy-MM-dd');
          if (startDateStr === searchDateStr) {
            matchesDate = true;
          }
        }
        
        // Check if search date matches end date
        if (!matchesDate && timesheet.endDate) {
          const endDate = new Date(timesheet.endDate);
          const endDateStr = format(endDate, 'yyyy-MM-dd');
          if (endDateStr === searchDateStr) {
            matchesDate = true;
          }
        }
        
        // Also check if search date falls within the timesheet date range
        if (!matchesDate && timesheet.startDate && timesheet.endDate) {
          const startDate = new Date(timesheet.startDate);
          const endDate = new Date(timesheet.endDate);
          if (searchDate >= startDate && searchDate <= endDate) {
            matchesDate = true;
          }
        }
        
        if (!matchesDate) {
          return false;
        }
      } catch (error) {
        // If date parsing fails, return false
        return false;
      }
    }

    // Description/search term filter
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        timesheet.description?.toLowerCase().includes(searchLower);
      if (!matchesSearch) {
        return false;
      }
    }

    return true;
  });

  const getTimesheetSortDate = (t) => {
    const raw = t?.submittedAt || t?.createdAt || t?.startDate || t?.endDate;
    const d = raw ? new Date(raw) : null;
    const ts = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
    return ts;
  };

  // Sort timesheets: default by status priority, optionally by date order
  const sortedTimesheets = [...filteredTimesheets].sort((a, b) => {
    if (sortMode === 'DATE_DESC' || sortMode === 'DATE_ASC') {
      const dateA = getTimesheetSortDate(a);
      const dateB = getTimesheetSortDate(b);
      const diff = dateA - dateB;
      return sortMode === 'DATE_ASC' ? diff : -diff;
    }

    const statusOrder = {
      SUBMITTED: 1,
      PENDING: 2,
      DRAFT: 3,
      REUPDATED: 4,
      REJECTED: 5,
      APPROVED: 6,
    };

    const orderA = statusOrder[a.status] || 99;
    const orderB = statusOrder[b.status] || 99;
    if (orderA !== orderB) return orderA - orderB;

    // Secondary sort: newest first inside same status bucket
    return getTimesheetSortDate(b) - getTimesheetSortDate(a);
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'SUBMITTED':
      case 'PENDING':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'REUPDATED':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'PENDING':
        return 'bg-orange-100 text-orange-800';
      case 'REUPDATED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateTotalHours = (entries) => {
    return entries?.reduce((total, entry) => total + (entry.hours || entry.hoursWorked || 0), 0) || 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Error loading user timesheets: {error.response?.data?.message || error.message}</p>
        <button 
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">User not found</p>
        <button 
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  const userRole = typeof user.role === 'object' ? user.role?.name : user.role;
  const isManager = userRole === 'MANAGER';

  return (
    <div className="space-y-6">
      {/* Header with Stats and Employee Details */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row gap-6"
      >
        {/* Employee Details - Left Side */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-start space-x-4"
        >
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isManager ? 'Manager' : 'Employee'} Timesheets
            </h1>
            <div className="flex items-center space-x-3 mt-2">
              <div className="h-12 w-12 rounded-full bg-primary-500 flex items-center justify-center">
                <span className="text-lg font-medium text-white">
                  {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                </span>
              </div>
              <div>
                <p className="text-lg font-medium text-gray-900">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-sm text-gray-500">{user.email}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {userRole} • ID: {user.id}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Summary Stats - Right Side */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setStatusFilter('ALL')}
            className={`bg-white rounded-lg shadow-sm p-3 text-left transition-all ${
              statusFilter === 'ALL' 
                ? 'ring-2 ring-primary-500 shadow-md' 
                : 'hover:shadow-md hover:bg-gray-50'
            }`}
          >
            <p className="text-xs font-medium text-gray-600">Total Timesheets</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{timesheets.length}</p>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setStatusFilter('APPROVED')}
            className={`bg-white rounded-lg shadow-sm p-3 text-left transition-all ${
              statusFilter === 'APPROVED' 
                ? 'ring-2 ring-green-500 shadow-md' 
                : 'hover:shadow-md hover:bg-gray-50'
            }`}
          >
            <p className="text-xs font-medium text-gray-600">Approved</p>
            <p className="text-xl font-bold text-green-600 mt-1">
              {timesheets.filter(t => t.status === 'APPROVED').length}
            </p>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setStatusFilter(NEEDS_APPROVAL)}
            className={`bg-white rounded-lg shadow-sm p-3 text-left transition-all ${
              statusFilter === NEEDS_APPROVAL
                ? 'ring-2 ring-yellow-500 shadow-md' 
                : 'hover:shadow-md hover:bg-gray-50'
            }`}
          >
            <p className="text-xs font-medium text-gray-600">Pending Approval</p>
            <p className="text-xl font-bold text-yellow-600 mt-1">
              {timesheets.filter(t => t.status === 'SUBMITTED' || t.status === 'REUPDATED').length}
            </p>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setStatusFilter('ALL')}
            className={`bg-white rounded-lg shadow-sm p-3 text-left transition-all ${
              statusFilter === 'ALL' 
                ? 'ring-2 ring-blue-500 shadow-md' 
                : 'hover:shadow-md hover:bg-gray-50'
            }`}
          >
            <p className="text-xs font-medium text-gray-600">Total Hours</p>
            <p className="text-xl font-bold text-blue-600 mt-1">
              {timesheets.reduce((total, t) => total + (t.totalHours || 0), 0).toFixed(1)}
            </p>
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Timesheet ID Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by Timesheet ID..."
                value={timesheetIdSearch}
                onChange={(e) => setTimesheetIdSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
              />
            </div>

            {/* Date Search */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10" />
              <input
                type="date"
                value={dateSearch}
                onChange={(e) => setDateSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 cursor-pointer"
              />
            </div>

            {/* Description Search */}
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by Description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white"
              >
                <option value="ALL">All Status</option>
                <option value={NEEDS_APPROVAL}>Pending Approval</option>
                <option value="DRAFT">Draft</option>
                <option value="APPROVED">Approved</option>
              </select>
            </div>

            {/* Sort by Date */}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white"
              >
                <option value="STATUS">Sort: Status Priority</option>
                <option value="DATE_DESC">Sort: Date (Newest First)</option>
                <option value="DATE_ASC">Sort: Date (Oldest First)</option>
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(timesheetIdSearch || dateSearch || searchTerm || statusFilter !== 'ALL') && (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setTimesheetIdSearch('');
                  setDateSearch('');
                  setSearchTerm('');
                  setStatusFilter('ALL');
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Timesheets List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm"
      >
        {sortedTimesheets.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {sortedTimesheets.map((timesheet, index) => (
              <motion.div
                key={timesheet.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => {
                  const currentUserRole = typeof currentUser?.role === 'object' ? currentUser?.role?.name : currentUser?.role;
                  let detailUrl = '';
                  if (currentUserRole === 'SUPER_ADMIN') {
                    detailUrl = `/super-admin/timesheets/${timesheet.id}`;
                  } else if (currentUserRole === 'MANAGER') {
                    detailUrl = `/manager/timesheets/${timesheet.id}`;
                  } else {
                    detailUrl = `/admin/timesheets/${timesheet.id}`;
                  }
                  navigate(detailUrl);
                }}
                className="p-4 md:p-6 hover:bg-gray-50 hover:border-l-4 hover:border-primary-500 transition-all duration-200 cursor-pointer group border-b border-gray-100 last:border-b-0"
              >
                {/* Mobile: only Timesheet ID + View Details */}
                <div className="flex md:hidden items-center justify-between gap-3">
                  <span className="text-base font-semibold text-gray-900">
                    Timesheet #{timesheet.id}
                  </span>
                  <span className="text-sm font-medium text-primary-600 group-hover:text-primary-500 shrink-0">
                    View Details →
                  </span>
                </div>
                {/* Desktop: full card */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                          Timesheet #{timesheet.id}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(timesheet.status)}`}>
                          {getStatusIcon(timesheet.status)}
                          <span>{timesheet.status}</span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-2">
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {format(new Date(timesheet.startDate), 'MMM dd')} - {format(new Date(timesheet.endDate), 'MMM dd, yyyy')}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{timesheet.totalHours || calculateTotalHours(timesheet.entries)} hours</span>
                        </div>
                        {timesheet.entries && (
                          <div className="flex items-center space-x-1 text-sm text-gray-600">
                            <FileText className="w-4 h-4" />
                            <span>{timesheet.entries.length} entries</span>
                          </div>
                        )}
                      </div>
                      {timesheet.description && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-1">
                          {timesheet.description}
                        </p>
                      )}
                      {timesheet.submittedAt && (
                        <p className="text-xs text-gray-400 mt-2">
                          Submitted: {format(new Date(timesheet.submittedAt), 'MMM dd, yyyy hh:mm a')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-400 group-hover:text-primary-500 transition-colors">
                      View Details →
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {timesheetIdSearch || dateSearch || searchTerm || statusFilter !== 'ALL'
                ? 'Try adjusting your filters (ID, Date, Description, or Status)'
                : `${user.firstName} ${user.lastName} has no timesheets yet`}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default EmployeeTimesheetDetail;

